import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@smena/ui';

// Обёртка над Recharts с общими для админки настройками осей/сетки/тултипа.
// Цвета берём из CSS-переменных темы, а не хардкодим: сменится акцент - сменятся графики.
//
// Анимация выключена намеренно: дашборд поллит раз в 30 с, и перерисовка «с нуля» на каждом
// обновлении читается как мигание, а не как информация.
//
// Recharts живёт только в admin-бандле; panel не трогаем (в ней графиков нет).

const AXIS = { stroke: 'var(--color-muted)', fontSize: 11 } as const;
const GRID = 'var(--color-line)';

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-panel)',
  border: '1px solid var(--color-line)',
  borderRadius: 8,
  fontSize: 12,
} as const;

/** Короткая подпись оси X: «17.08» вместо ISO - иначе метки наезжают друг на друга. */
function shortDay(day: unknown): string {
  const s = String(day ?? '');
  const [, m, d] = s.split('-');
  return d && m ? `${d}.${m}` : s;
}

export interface SeriesSpec {
  key: string;
  label: string;
  color: string;
}

export function TimeSeriesCard({
  title,
  subtitle,
  data,
  series,
  height = 220,
  kind = 'area',
}: {
  title: string;
  subtitle?: string;
  /** Строки ряда: день + произвольные числовые поля. Тип намеренно широкий - Recharts
   *  читает значения по dataKey из SeriesSpec, а не по фиксированной форме объекта. */
  data: ReadonlyArray<{ day: string } & Record<string, unknown>>;
  series: SeriesSpec[];
  height?: number;
  kind?: 'area' | 'line';
}) {
  const empty = data.every((row) => series.every((s) => !Number(row[s.key])));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {subtitle ? <p className="text-xs text-muted">{subtitle}</p> : null}
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="grid h-[220px] place-items-center text-sm text-muted">
            За период данных нет
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            {kind === 'area' ? (
              <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickFormatter={shortDay} {...AXIS} />
                <YAxis allowDecimals={false} {...AXIS} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={shortDay} />
                {series.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    fill={s.color}
                    fillOpacity={0.18}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                ))}
              </AreaChart>
            ) : (
              <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickFormatter={shortDay} {...AXIS} />
                <YAxis allowDecimals={false} {...AXIS} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={shortDay} />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
